// docs/72 IT17–IT18 — the module's side of items the client reads: the camp-use reducer
// (`use_item_in_camp`), the boons a hero carries into its next fights (`hero_boon`), the boons riding
// in a fight (`encounter_boon`), and the storyteller's own name and lore for a relic (`relic_name`).
import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '../../bindings';
import { call, type CallResult } from '../net/reducers';
import { requireConn } from '../net/connection';

const NONE = -1n;

/** Uses a pack piece out of a fight: its boon (on the hero, or `targetHero`), xp, gold, card or hint. */
export function useItemInCamp(itemId: bigint, targetHero?: bigint): Promise<CallResult> {
  return call('use_item_in_camp', () => requireConn().reducers.useItemInCamp({ itemId, targetHero }));
}

export interface HeroBoon { heroId: bigint; card: number; fightsLeft: number; fromDef: number }

/** The boons waiting on a World's heroes for their next fights. */
export function useHeroBoons(worldId: bigint | undefined): HeroBoon[] {
  const [rows] = useTable(tables.heroBoon.where((r) => r.worldId.eq(worldId ?? NONE)), { enabled: worldId !== undefined });
  return useMemo(() => rows.filter((r) => r.fightsLeft > 0).map((r) => ({ heroId: r.heroId, card: r.card, fightsLeft: r.fightsLeft, fromDef: r.fromDef })), [rows]);
}

/** The boons riding in a fight, by hero. */
export function useEncounterBoons(encounterId: bigint | undefined): Array<{ heroId: bigint; card: number }> {
  const [rows] = useTable(tables.encounterBoon.where((r) => r.encounterId.eq(encounterId ?? NONE)), { enabled: encounterId !== undefined });
  return useMemo(() => rows.map((r) => ({ heroId: r.heroId, card: r.card })), [rows]);
}

/** The storyteller's name and lore for each relic of a World, by item id (written ones only). */
export function useRelicNames(worldId: bigint | undefined): Map<string, { name: string; lore: string }> {
  const [rows] = useTable(tables.relicName.where((r) => r.worldId.eq(worldId ?? NONE)), { enabled: worldId !== undefined });
  return useMemo(() => {
    const m = new Map<string, { name: string; lore: string }>();
    for (const r of rows) if (r.itemId !== undefined && r.written && r.name) m.set(r.itemId.toString(), { name: r.name, lore: r.lore });
    return m;
  }, [rows]);
}
